import { useLayoutEffect, useMemo, useRef } from 'preact/hooks';
import {
  useCheckoutContext,
  useCheckoutStore,
  useSelector,
  useSceneLoaderContext,
} from '@primer-io/shared-library/contexts';
import {
  useIsCurrentScene,
  useScene,
  useSceneEntering,
  useSceneMounting,
} from '../utils/hooks';
import { Scene } from '../enums/Checkout';
import CreditCardForm from '../payment-methods/credit-card/CreditCardForm';
import VaultMenu from '../components/VaultMenu';
import VaultedPaymentMethod from '../components/VaultedPaymentMethod';
import { AnimatePresence } from '../utils/AnimatePresence';
import {
  useWait,
  WaitForComponents,
} from '../utils/SceneLoader/WaitForComponents';
import PaymentMethodButton from '../components/PaymentMethodButton';
import { BasePaymentMethod } from '../payment-methods/BasePaymentMethod';
import { VaultManagerOptions } from '../types';

const SCENE = Scene.MANAGE_PAYMENT_METHODS;

const VaultManagerScene = () => {
  const store = useCheckoutStore();
  const { viewUtils, checkout, options } = useCheckoutContext();
  const { setIsMounted } = useSceneLoaderContext();
  const vault = useSelector((s) => s.vault);
  const labels = useSelector((s) => s.translation);

  const emptyTextRef = useRef<HTMLDivElement>();
  const isCurrentScene = useIsCurrentScene(Scene.MANAGE_PAYMENT_METHODS);

  const paymentMethods = useSelector((s) => s.paymentMethods);

  const paymentMethodButtons = useMemo(() => {
    return Object.entries(paymentMethods).filter(([key]) => key !== 'card');
  }, [paymentMethods]);

  const vaultOptions = options as VaultManagerOptions;

  const { ready } = useWait(() => {
    setIsMounted();
  }, ['components', 'animation']);

  const toggleEmpty = async (
    isEmpty: boolean,
    animated: boolean,
  ): Promise<void> => {
    viewUtils.toggleVisibilityAnimated('primer-checkout-actions', !isEmpty, {
      duration: animated ? 300 : 0,
    });

    viewUtils.toggleVisibilityAnimated(
      'primer-checkout-saved-payment-methods-empty-text',
      isEmpty,
      {
        duration: animated ? 300 : 0,
        animateHeight: true,
        autoHeight: true,
      },
    );

    viewUtils.toggleVisibilityAnimated(
      'primer-checkout-payment-method-divider',
      !isEmpty,
      {
        duration: animated ? 300 : 0,
        animateHeight: true,
        autoHeight: true,
      },
    );
  };

  useSceneMounting(() => {
    (async () => {
      await toggleEmpty(!store.hasVault, false);
      ready('animation');
    })();
  }, SCENE);

  useLayoutEffect(() => {
    if (isCurrentScene) {
      toggleEmpty(!store.hasVault, true);
    }
  }, [store.hasVault]);

  useScene(() => {
    const handleSubmitButtonClick = async () => {
      await checkout.tokenize();
    };

    store.addSubmitButtonClickListener(handleSubmitButtonClick);
    return () => {
      store.removeSubmitButtonClickListener(handleSubmitButtonClick);
    };
  }, SCENE);

  useSceneEntering(() => {
    store.setSubmitButtonVisible(true);
    store.setSubmitButtonContent(labels?.addCard ?? 'add card');
  }, SCENE);

  ///////////////////////////////////////////
  // Render
  ///////////////////////////////////////////

  const renderEditButton = () => {
    return (
      !(vaultOptions.deletionDisabled ?? false) && (
        <div
          id='primer-checkout-vault-edit-button'
          data-testId='CheckoutVaultScene.vaultEditButton'
          class='PrimerCheckout__savedPaymentMethodActions'
        >
          <VaultMenu />
        </div>
      )
    );
  };

  return (
    <WaitForComponents onReady={() => ready('components')}>
      <div
        id='primer-checkout-scene-manage-payment-methods'
        class='PrimerCheckout__scene PrimerCheckout__scene--managePaymentMethods PrimerCheckout--exited'
      >
        <div>
          <div
            id='primer-checkout-vault'
            class='PrimerCheckout__sceneElement PrimerCheckout__savedPaymentMethods'
          >
            <div class='PrimerCheckout__savedPaymentMethods__title'>
              <div class='PrimerCheckout__label PrimerCheckout__label--muted'>
                {labels?.managePaymentMethods}
              </div>
              {renderEditButton()}
            </div>

            <div
              ref={emptyTextRef}
              id='primer-checkout-saved-payment-methods-empty-text'
              class='PrimerCheckout__emptyText'
            >
              <span class='PrimerCheckout__label'>
                {labels?.savedPaymentMethodsEmpty}
              </span>
            </div>

            <div class='PrimerCheckout__savedPaymentMethodContainer'>
              <AnimatePresence>
                {vault.items.map((vaultedItem) => (
                  <VaultedPaymentMethod
                    key={vaultedItem.id}
                    vaultedItem={vaultedItem}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div
            id='primer-checkout-payment-method-divider'
            class='PrimerCheckout__sceneElement PrimerCheckout__textDivider PrimerCheckout__divider'
          >
            <span class='PrimerCheckout__label PrimerCheckout__label--muted'>
              {labels?.newPaymentMethod}
            </span>
          </div>
          {paymentMethodButtons.length > 0 && (
            <div
              id='primer-checkout-apm-button-container'
              class='PrimerCheckout__sceneElement PrimerCheckout__apmButtonContainer'
            >
              {paymentMethodButtons.map(([key, paymentMethod]) => (
                <PaymentMethodButton
                  key={paymentMethod.displayName}
                  id={key}
                  paymentMethod={paymentMethod}
                />
              ))}
            </div>
          )}

          <CreditCardForm />
          <div></div>
        </div>
      </div>
    </WaitForComponents>
  );
};

export default VaultManagerScene;
